import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useConfirmation } from "@/hooks/use-confirmation";
import { useDocumentDataContext } from "@/providers/DocumentDataProvider";
import { useDialogDataContext } from "@/providers/DialogDataProvider";

// Import hooks
import { useWizardManager } from "@/hooks/useWizardManager";
import { useStatusWorkflows } from "@/hooks/useStatusWorkflows";
import { useUnifiedItemsFilters } from "@/hooks/useUnifiedItemsFilters";
import { useFolderAwareHandlers } from "@/hooks/useFolderAwareHandlers";
import { usePendingChanges } from "@/hooks/usePendingChanges";
import { useNavigationWarning } from "@/hooks/useNavigationWarning";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";

// Import flow configs
import { dialogFlowConfig } from "@/components/flow/configs/dialogFlowConfig";

// Import stores for pending changes
import { objectsStore } from "@/lib/stores/objectsStore";
import { templatesStore } from "@/lib/stores/templatesStore";
import { documentStore } from "@/lib/stores/documentStore";

// Import local hooks and components
import { useUnifiedItemsData } from "./hooks/useUnifiedItemsData";
import { useUnifiedItemsGrouping } from "./hooks/useUnifiedItemsGrouping";
import { UnifiedItemsContent } from "./components/UnifiedItemsContent";
import { UnifiedItemsNotification } from "./components/UnifiedItemsNotification";

// Import states
import { PageLoadingState } from "@/components/states/PageLoadingState";
import { PageEmptyState } from "@/components/states/PageEmptyState";

// Import types
import { 
  UnifiedItemsContainerProps, 
  PendingChangesProps,
  FlowViewProps,
} from "./types";

// Lazy import for wizards
const UnifiedWizardRenderer = React.lazy(() => 
  import("@/components/wizards/UnifiedWizardRenderer").then(m => ({ default: m.UnifiedWizardRenderer }))
);

/**
 * Generic container component for unified item pages
 * Receives PageHandlers from pages for type-specific behavior
 * Handles orchestration of data, state, and UI rendering
 */
export function UnifiedItemsContainer({
  itemType,
  handlers,
  config,
}: UnifiedItemsContainerProps) {
  // Core hooks
  const { toast } = useToast();
  const navigate = useNavigate();
  const { confirmation } = useConfirmation();
  const { createDocument } = useDocumentDataContext();
  const { createDialogItem, updateDialogItem } = useDialogDataContext();
  const { currentUser } = useSimplifiedUserData();

  // Data hook - aggregates all data-related functionality
  const data = useUnifiedItemsData({ itemType, config, handlers });

  // Grouping hook - handles card grouping by folder
  const { groupedCards } = useUnifiedItemsGrouping({
    cards: data.cards,
    folders: data.folders,
    selectedFolderId: data.selectedFolderId,
  });

  // Wizard manager for modal states
  const wizardManager = useWizardManager();

  // Status workflows for filters
  const statusWorkflows = useStatusWorkflows({ gameId: data.selectedGame?.id });

  // Filter configurations
  const filterConfigs = useUnifiedItemsFilters({
    itemType,
    data: data.data,
    tags: data.tags,
    statusWorkflows,
    blueprints: data.glossaryBlueprints,
  });

  // #region Flow View State
  const [selectedItemForFlow, setSelectedItemForFlow] = useState<any>(null);
  
  const flowConfig = useMemo(() => {
    if (itemType === 'dialogs') {
      return dialogFlowConfig;
    }
    return undefined;
  }, [itemType]);
  
  const allowFlowView = handlers.features?.supportsFlowView ?? itemType === 'dialogs';
  
  // Auto-select first item for flow view if none is selected
  useEffect(() => {
    if (allowFlowView && !selectedItemForFlow && data.data && data.data.length > 0) {
      setSelectedItemForFlow(data.data[0]);
    }
  }, [allowFlowView, selectedItemForFlow, data.data]);

  const flowViewProps: FlowViewProps | null = useMemo(() => {
    if (!allowFlowView) return null;
    return {
      flowConfig,
      selectedItemForFlow,
      allowFlowView,
    };
  }, [allowFlowView, flowConfig, selectedItemForFlow]);
  // #endregion

  // #region Pending Changes System
  const supportsPendingChanges = handlers.features?.supportsPendingChanges ?? 
    (itemType === 'library' || itemType === 'blueprints' || itemType === 'documents');
  
  const pendingChangesItemType = itemType === 'library' ? 'library' : itemType === 'blueprints' ? 'template' : itemType === 'documents' ? 'document' : undefined;
  
  const pendingChanges = usePendingChanges({
    gameId: data.selectedGame?.id,
    itemType: pendingChangesItemType || 'library',
  });

  // Build modified items and cells maps
  const modifiedItems = useMemo(() => {
    return new Set(pendingChanges.pendingChanges.map(c => c.itemId));
  }, [pendingChanges.pendingChanges]);

  const modifiedCells = useMemo(() => {
    const map = new Map<string, string[]>();
    pendingChanges.pendingChanges.forEach(c => {
      map.set(c.itemId, c.modifiedCells);
    });
    return map;
  }, [pendingChanges.pendingChanges]);

  // Cell value change handler for table editing
  const handleCellValueChange = useCallback((rowKey: string, cellKey: string, newValue: any) => {
    // If page provides custom handler, use it
    if (handlers.onCellValueChange) {
      handlers.onCellValueChange(rowKey, cellKey, newValue);
      return;
    }

    // Default implementation for library and blueprints
    if (!data.selectedGame?.id || !pendingChangesItemType) return;
    
    const rawItem = data.rawItems.find((item: any) => item.id === rowKey);
    if (!rawItem) {
      console.warn('[handleCellValueChange] Item not found:', rowKey);
      return;
    }
    
    // Check if item is locked
    const currentUserId = currentUser?.id;
    const editPermissions = rawItem.edit_permissions || 'Open';
    const ownerId = rawItem.owner;
    const isLocked = editPermissions === 'Locked' && 
                     ownerId !== undefined && 
                     currentUserId !== undefined && 
                     ownerId !== currentUserId;
    
    if (isLocked) {
      console.warn('[handleCellValueChange] Cannot edit locked item:', rowKey);
      return;
    }
    
    const metadataFieldMap: Record<string, string> = {
      'Name': 'name',
      'Title': 'name',
      'Folder': 'folder',
      'Owner': 'owner',
      'Tags': 'tags',
      'Identifiers': 'identifiers',
      'Blueprint': 'blueprint_id',
      'Status': 'status',
      'Type': 'type',
      'Description': 'description',
    };
    
    const updatedData = JSON.parse(JSON.stringify(rawItem));
    const metadataKey = metadataFieldMap[cellKey];
    
    if (metadataKey) {
      updatedData[metadataKey] = newValue;
    } else {
      let fieldFound = false;
      if (Array.isArray(updatedData.data)) {
        for (const section of updatedData.data) {
          if (Array.isArray(section.fields)) {
            for (const field of section.fields) {
              if (field.label === cellKey || field.id === cellKey) {
                field.value = newValue;
                fieldFound = true;
                break;
              }
            }
          }
          if (fieldFound) break;
        }
      }
      
      if (!fieldFound) {
        console.warn(`[handleCellValueChange] Field "${cellKey}" not found in item data`);
        return;
      }
    }
    
    pendingChanges.trackChange(
      rowKey,
      rawItem.version || null,
      rawItem,
      updatedData,
      cellKey
    );
    
    // Update local store for UI feedback
    if (pendingChangesItemType === 'library') {
      const currentData = objectsStore.getData();
      const newData = currentData.map((item: any) =>
        item.id === rowKey ? updatedData : item
      );
      objectsStore.setData(newData);
    } else if (pendingChangesItemType === 'template') {
      const currentData = templatesStore.getData();
      const newData = currentData.map((item: any) =>
        item.id === rowKey ? updatedData : item
      );
      templatesStore.setData(newData);
    } else if (pendingChangesItemType === 'document') {
      const currentData = documentStore.getData();
      const newData = currentData.map((item: any) =>
        item.id === rowKey ? updatedData : item
      );
      documentStore.setData(newData);
    }
  }, [currentUser?.id, data.selectedGame?.id, pendingChangesItemType, data.rawItems, pendingChanges, handlers]);

  // Navigation warning for unsaved changes
  useNavigationWarning({
    hasUnsavedChanges: supportsPendingChanges && pendingChanges.hasPendingChanges,
  });

  const pendingChangesProps: PendingChangesProps | null = useMemo(() => {
    if (!supportsPendingChanges || !pendingChangesItemType) return null;
    return {
      pendingChangesCount: pendingChanges.pendingCount,
      hasPendingChanges: pendingChanges.hasPendingChanges,
      modifiedItems,
      modifiedCells,
      onSaveItem: async (itemId: string) => {
        await pendingChanges.saveItem(itemId);
      },
      onDiscardItem: pendingChanges.discardItem,
      onSaveAllItems: pendingChanges.saveAllItems,
      onDiscardAllItems: pendingChanges.discardAllItems,
      onForceSaveItem: pendingChanges.forceSaveItem,
    };
  }, [supportsPendingChanges, pendingChangesItemType, pendingChanges, modifiedItems, modifiedCells]);
  // #endregion

  // #region Folder-Aware Handlers
  const folderHandlers = useFolderAwareHandlers({
    itemType,
    selectedFolderId: data.selectedFolderId,
    selectedGame: data.selectedGame,
    data: data.data,
    toast,
    originalHandleGlossaryItemCreated: (item: any) => {
      handlers.wizardCallbacks.onItemCreated(item);
    },
    originalHandleDocumentCreated: (item: any) => {
      handlers.wizardCallbacks.onItemCreated(item);
    },
    originalHandleSaveBlueprint: (item: any) => {
      handlers.wizardCallbacks.onItemCreated(item);
    },
    setIsAddWizardOpen: wizardManager.setIsAddWizardOpen,
    setIsCreateWizardOpen: wizardManager.setIsCreateWizardOpen,
    setIsEditModalOpen: wizardManager.setIsEditModalOpen,
    setIsDialogWizardOpen: wizardManager.setIsDialogWizardOpen,
    setSelectedBlueprint: wizardManager.setSelectedBlueprint,
    setSelectedGlossaryItem: wizardManager.setSelectedGlossaryItem,
    setSelectedDialog: wizardManager.setSelectedDialog,
    createDialogItem,
    updateDialogItem,
  });
  // #endregion

  // #region Bulk Delete Handler
  const handleDeleteSelected = useCallback(async (ids: string[]) => {
    if (!data.selectedGame?.id || ids.length === 0) return;

    const itemCount = ids.length;
    const itemLabel = itemCount === 1 ? config.title.replace(/s$/, '') : config.title;

    const confirmDialog = confirmation({
      title: `Delete ${itemCount} ${itemLabel}`,
      description: `Are you sure you want to delete ${itemCount} selected ${itemLabel.toLowerCase()}? This action cannot be undone.`,
      confirmText: "Delete All",
      cancelText: "Cancel",
      variant: "destructive",
      onConfirm: async () => {
        try {
          let successCount = 0;
          let failCount = 0;
          
          for (const id of ids) {
            try {
              if (handlers.handlers.onBulkDelete) {
                // Use page-provided bulk delete if available
                await handlers.handlers.onDelete(id);
              } else {
                await config.handleDelete(id, data.selectedGame.id, toast);
              }
              successCount++;
            } catch (error) {
              console.error(`Error deleting item ${id}:`, error);
              failCount++;
            }
          }
          
          confirmDialog.dismiss();
          
          if (successCount > 0) {
            toast({
              title: "Success",
              description: failCount > 0 
                ? `${successCount} of ${itemCount} ${itemLabel.toLowerCase()} deleted successfully. ${failCount} failed.`
                : `${itemCount} ${itemLabel.toLowerCase()} deleted successfully`,
            });
          } else {
            toast({
              title: "Error",
              description: `Failed to delete selected items`,
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error("Error deleting items:", error);
          confirmDialog.dismiss();
          toast({
            title: "Error",
            description: `Failed to delete some items`,
            variant: "destructive",
          });
        }
      },
      onCancel: () => {
        confirmDialog.dismiss();
      },
    });
  }, [data.selectedGame?.id, config, handlers.handlers, confirmation, toast]);
  // #endregion

  // #region Wizard Handlers
  const handleBulkCreateSuccess = useCallback(() => {
    wizardManager.setIsBulkCreateWizardOpen(false);
  }, [wizardManager]);

  const handleGlossaryItemUpdated = useCallback((item: any) => {
    handlers.wizardCallbacks.onItemUpdated(item);
  }, [handlers.wizardCallbacks]);
  // #endregion

  // #region Loading and Empty States
  if (data.isLoading) {
    return <PageLoadingState message={`Loading ${config.title.toLowerCase()}...`} />;
  }

  if (!data.selectedGame) {
    return (
      <PageEmptyState
        icon="🎮"
        title="No Game Selected"
        description={config.noGameSelectedDescription}
      />
    );
  }
  // #endregion

  return (
    <div className="h-full flex flex-col">
      {/* New items notification banner */}
      <UnifiedItemsNotification
        newItemCount={data.newItemCount}
        itemLabel={config.title.toLowerCase()}
        onRefresh={async () => {
          await data.refreshData();
          data.clearNewItems();
        }}
        onDismiss={data.clearNewItems}
      />

      {/* Main page layout */}
      <div className="flex-1 overflow-hidden">
        <UnifiedItemsContent
          itemType={itemType}
          config={config}
          handlers={handlers}
          data={data.data}
          cards={data.cards}
          groupedCards={groupedCards}
          selectedGame={data.selectedGame}
          tags={data.tags}
          selectedFolderId={data.selectedFolderId}
          pageLayoutConfig={data.pageLayoutConfig}
          isEditMode={data.isEditMode}
          setIsEditMode={data.setIsEditMode}
          handlePageConfigChange={data.handlePageConfigChange}
          handleCardClick={data.handleCardClick}
          handleDelete={data.handleDelete}
          handleAddWithFolder={folderHandlers.handleAddWithFolder}
          handleDeleteSelected={handleDeleteSelected}
          refreshData={data.refreshData}
          availableSections={data.availableSections}
          availableFields={data.availableFields}
          rawItems={data.rawItems}
          templateLookup={data.templateLookup}
          folderPathLookup={data.folderPathLookup}
          filterConfigs={filterConfigs}
          wizardManager={wizardManager}
          pendingChangesProps={pendingChangesProps}
          flowViewProps={flowViewProps}
          handleCellValueChange={handleCellValueChange}
        />

        {/* Wizards and Modals - lazy loaded */}
        <React.Suspense fallback={null}>
          <UnifiedWizardRenderer
            itemType={itemType}
            config={config}
            isAddWizardOpen={wizardManager.isAddWizardOpen}
            isEditModalOpen={wizardManager.isEditModalOpen}
            isCreateWizardOpen={wizardManager.isCreateWizardOpen}
            isBulkCreateWizardOpen={wizardManager.isBulkCreateWizardOpen}
            isDialogWizardOpen={wizardManager.isDialogWizardOpen}
            selectedBlueprint={wizardManager.selectedBlueprint}
            selectedGlossaryItem={wizardManager.selectedGlossaryItem}
            selectedDialog={wizardManager.selectedDialog}
            handleGlossaryItemCreated={folderHandlers.handleGlossaryItemCreated}
            handleGlossaryItemUpdated={handleGlossaryItemUpdated}
            handleDocumentCreated={folderHandlers.handleDocumentCreated}
            handleSaveBlueprint={folderHandlers.handleSaveBlueprint}
            handleDialogSave={folderHandlers.handleDialogSave}
            handleBulkCreateSuccess={handleBulkCreateSuccess}
            setIsAddWizardOpen={wizardManager.setIsAddWizardOpen}
            setIsEditModalOpen={wizardManager.setIsEditModalOpen}
            setIsCreateWizardOpen={wizardManager.setIsCreateWizardOpen}
            setIsBulkCreateWizardOpen={wizardManager.setIsBulkCreateWizardOpen}
            setIsDialogWizardOpen={wizardManager.setIsDialogWizardOpen}
            setSelectedDialog={wizardManager.setSelectedDialog}
            selectedGame={data.selectedGame}
            selectedFolderId={data.selectedFolderId}
          />
        </React.Suspense>
      </div>
    </div>
  );
}
